import { isArray } from 'lodash';
import moment, { Moment } from 'moment';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { UserType } from 'api/models';
import { TimeoffHistory, TimeoffResponse } from 'api/timeoff/types';
import { humanise, humaniseWithDay } from 'components/atoms/humanised-date';
import { useUser } from 'hooks/use-user';
import { getTimeOffDurationPerDay } from 'utils/time-off-utils';

interface Props {
  timeOff:
    | TimeoffResponse
    | undefined
    | TimeoffResponse[]
    | TimeoffHistory
    | TimeoffHistory[];
  userName?: string | undefined;
  startDate?: Moment | undefined;
  dueDate?: Moment | undefined;
}

const GetTooltipHeader = ({
  userName,
  timeOff,
}: {
  userName?: string;
  timeOff: TimeoffResponse | TimeoffHistory;
}) => {
  const intl = useIntl();
  const timeOffDurationPerDay = getTimeOffDurationPerDay(
    timeOff?.timeOffDuration,
    timeOff?.timeOffType,
    intl,
  );

  return (
    <div>
      <FormattedMessage
        id="timeoff.tooltip.header_message_vendor"
        values={{
          userName,
          strong: (chunks) => <strong>{chunks}</strong>,
          timeOffDuration: timeOffDurationPerDay || '',
        }}
      />
    </div>
  );
};

const GetTooltipContent: React.FC<{
  timeOff: TimeoffResponse | TimeoffHistory;
}> = ({ timeOff: { timeOffStart, timeOffEnd, note } }) => {
  return (
    <>
      {timeOffStart === timeOffEnd ? (
        <div>
          <strong>{humaniseWithDay(moment(timeOffStart))}</strong>
        </div>
      ) : (
        <div>
          <strong>{humanise(moment(timeOffStart))}</strong>
          <span>{' to '}</span>
          <strong>{humanise(moment(timeOffEnd))}</strong>
        </div>
      )}
      {note && (
        <div>
          <i>{`"${note}"`}</i>
        </div>
      )}
    </>
  );
};

const SingleDayTimeoffContentForClient: React.FC<{
  timeOff: TimeoffResponse | TimeoffHistory;
  userName?: string;
}> = ({ timeOff, userName }) => {
  const intl = useIntl();
  const timeOffDurationPerDay = getTimeOffDurationPerDay(
    timeOff?.timeOffDuration,
    timeOff?.timeOffType,
    intl,
  );

  return (
    <FormattedMessage
      id="timeoff.indicator.timeoff_warning_single_date"
      values={{
        strong: (chunks) => <strong>{chunks}</strong>,
        user: userName || '',
        date: moment(timeOff?.timeOffStart).format('DD MMM YY') || '',
        timeOffDuration: timeOffDurationPerDay || '',
      }}
    />
  );
};

const RangeTimeoffContentForClient: React.FC<{
  timeOff: TimeoffResponse | TimeoffHistory;
  userName?: string;
}> = ({ timeOff, userName }) => {
  const intl = useIntl();
  const timeOffDurationPerDay = getTimeOffDurationPerDay(
    timeOff?.timeOffDuration,
    timeOff?.timeOffType,
    intl,
  );

  return (
    <FormattedMessage
      id="timeoff.indicator.timeoff_warning_range"
      values={{
        strong: (chunks) => <strong>{chunks}</strong>,
        user: userName || '',
        startDate: moment(timeOff.timeOffStart).format('DD MMM YY') || '',
        endDate: moment(timeOff.timeOffEnd).format('DD MMM YY') || '',
        timeOffDuration: timeOffDurationPerDay || '',
      }}
    />
  );
};

const MultipleDayTimeoffContentForClient: React.FC<{
  userName?: string;
  startDate: Moment;
  dueDate: Moment;
  timeOff: TimeoffResponse | TimeoffHistory;
}> = ({ userName, startDate, dueDate }) => {
  return (
    <FormattedMessage
      id="timeoff.indicator.multiple_timeoff_warning_range"
      values={{
        strong: (chunks) => <strong>{chunks}</strong>,
        user: userName || '',
        startDate: startDate.format('DD MMM YY'),
        endDate: dueDate.format('DD MMM YY'),
      }}
    />
  );
};

export const GetTimeOffTooltipContent: React.FC<Props> = ({
  timeOff,
  userName,
  startDate,
  dueDate,
}) => {
  const { user } = useUser();

  if (!timeOff) {
    return null;
  }

  if (isArray(timeOff)) {
    if (user.userType === UserType.External) {
      if (startDate == null || dueDate == null) {
        return null;
      }
      if (timeOff.length === 1) {
        if (
          moment(timeOff[0].timeOffStart).isSame(timeOff[0].timeOffEnd, 'day')
        ) {
          return (
            <SingleDayTimeoffContentForClient
              userName={userName}
              timeOff={timeOff[0]}
            />
          );
        }

        return (
          <RangeTimeoffContentForClient
            userName={userName}
            timeOff={timeOff[0]}
          />
        );
      }

      return (
        <MultipleDayTimeoffContentForClient
          userName={userName}
          startDate={startDate}
          dueDate={dueDate}
          timeOff={timeOff[0]}
        />
      );
    }

    return (
      <>
        <GetTooltipHeader userName={userName} timeOff={timeOff[0]} />
        {timeOff.map((timeOffT, index) => (
          <>
            <GetTooltipContent timeOff={timeOffT} />
            {index !== timeOff.length - 1 && <br />}
          </>
        ))}
      </>
    );
  }

  if (user.userType === UserType.External) {
    if (moment(timeOff.timeOffStart).isSame(timeOff.timeOffEnd, 'day')) {
      return (
        <SingleDayTimeoffContentForClient
          userName={userName}
          timeOff={timeOff}
        />
      );
    }

    return (
      <RangeTimeoffContentForClient userName={userName} timeOff={timeOff} />
    );
  }

  return (
    <>
      <GetTooltipHeader userName={userName} timeOff={timeOff} />
      <GetTooltipContent timeOff={timeOff} />
    </>
  );
};
